<template>
  <div class="plans-page container-lg">
    <div class="page-header">
      <h1>Share your family tree today</h1>
      <h4>Create a fun, endless learning experience your family will love</h4>
    </div>
    <ul class="row list-unstyled plan-list">
      <li v-for="p in plansRef" :key="p.id" class="col-sm">
        <div v-if="p.baseType == 'plus'" class="recommend exp">
          <img src="@/assets/icons/star.svg"/>
          <img src="@/assets/icons/star.svg"/>
          <img src="@/assets/icons/star.svg"/>
          <img src="@/assets/icons/star.svg"/>
          Best experience
        </div>
        <div v-else class="recommend"></div>
        <div class="plan">
          <div class="plan-name" :class="{ free: !p.price }">
            {{ p.displayName }}
          </div>
          <div class="plan-description">
            {{ p.description }}
          </div>
          <div class="plan-price">
            <div class="price-name">
              {{ p.priceName }}
            </div>
            <div v-if="p.price" class="price">
              <div>${{ p.price.toFixed(2) }}/month</div>
              <div class="billed">${{ getPlanTermPrice(p).toFixed(0) }} for {{ p.termMonths }} months</div>
            </div>
            <div v-else class="price free">
              <div>&nbsp;</div>
              <div>No payment now or later</div>
            </div>
          </div>
          <div class="plan-select">
            <template v-if="!loadingRef && p.id == currentPlanIdRef">
              <router-link v-if="isAnonRef" to="/signup" class="btn btn-primary">
                Get started
              </router-link>
              <button type="button" v-else class="btn btn-secondary current" disabled>
                Current plan
              </button>
            </template>
            <button type="button" v-else class="btn btn-primary" :disabled="loadingRef" @click="onSelectPlan(p)">
              Select this plan
            </button>
          </div>
          <ul class="list-unstyled plan-features">
            <li class="tree-limit">
              <img src="@/assets/icons/simple_tree.svg" alt="Tree"/>
              <div>{{ p.treeLimit }}<span class="unit">{{ pluralize(p.treeLimit ?? 0, ["tree", "trees"]) }}</span></div>
            </li>
            <li class="person-limit">
              <img src="@/assets/icons/couple_gray_150.png" alt="Person"/>
              <div><span class="unit">no person limit</span></div>
            </li>
            <li class="content-limit">
              <ImageIcon size="1x" alt="Photo"/>
              <div>{{ getPlanContentLimit(p)?.value.toFixed(0) }}<span class="unit">{{ getPlanContentLimit(p)?.unit }} photos</span></div>
            </li>
            <li class="sharing-limit">
              <img src="@/assets/icons/share_60.png" alt="Sharing"/>
              <div v-if="p.shareLimit">{{ p.shareLimit }}<span class="unit">person sharing limit</span></div>
              <div v-else><span class="unit">no sharing limit</span></div>
            </li>
            <li v-if="!p.shareLimit" class="integration">
              <img src="@/assets/logos/GoldieMay_gold_64.png" alt="GoldieMay"/>
              <div><span class="unit">Goldie May integration</span></div>
            </li>
          </ul>
        </div>
      </li>
    </ul>
    <div class="footnote">Limits may be imposed for excessive use.</div>
    <SubscriptionConfirmPlanModal ref="confirmModalRef" @add="addAsync"/>
    <PaymentModal ref="paymentModalRef"/>
  </div>
</template>

<style lang="scss" scoped>
.plans-page {
  max-width: 900px;
}

.page-header {
  margin: 1rem 0 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;

  @media (min-width: 768px) {
    margin: 3rem 0;
  }

  h1, h4 {
    margin: 0;
  }

  h4 {
    font-size: 1.2rem;
  }
}

.plan-list {
  row-gap: 1rem;

  > li {
    display: flex;
    flex-direction: column;
  }
}

.recommend {
  display: none;
  margin: 0 1rem;
  height: 30px;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  padding: 0.25rem 0.5rem;
  text-align: center;

  @media (min-width: 768px) {
    display: block;
  }

  &.exp {
    display: block;
    position: relative;
    background-color: var(--bs-warning);

    img {
      position: absolute;
      filter: invert(1);

      &:nth-child(1) {
        top: 6px;
        left: 8px;
        width: 12px;
        transform: rotate(-8deg);
      }

      &:nth-child(2) {
        top: 8px;
        left: 24px;
        width: 16px;
        transform: rotate(5deg);
      }

      &:nth-child(3) {
        top: 12px;
        right: 30px;
        width: 12px;
        transform: rotate(0deg);
      }

      &:nth-child(4) {
        top: 6px;
        right: 8px;
        width: 16px;
        transform: rotate(8deg);
      }
    }
  }
}

.plan {
  flex: 1;
  border-radius: 8px;
  border: 1px solid #ddd;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.plan-name {
  margin: -1rem; // full bleed
  margin-bottom: 0;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  border-bottom: 1px solid #ddd;
  background-color: var(--page-bg-alt3);
  padding: 0.5rem 1rem;
  font-size: 1.2rem;
  text-align: center;

  &.free {
    background-color: #eee;
  }
}

.plan-price {
  min-height: 6rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;

  .price-name {
    font-size: 1.6rem;
  }

  .price.free, .price .billed {
    color: #888;
  }
}

.plan-description {
  min-height: 100px;
  font-size: 0.875rem;
  text-align: center;

  @media (min-width: 768px) {
    min-height: 6.5rem;
  }
}

.plan-select {
  display: flex;
  justify-content: center;

  .btn {
    min-width: 120px;

    .current {
      border: none;
      background-color: #eee !important;
      opacity: 1;
      color: #888;
    }
  }
}

.plan-features {
  display: grid;
  grid-template-columns: 30px auto;
  column-gap: 0.75rem;
  align-items: center;
  justify-content: center;
  // display: flex;
  // flex-direction: column;
  // align-items: center;
  padding: 1rem 0;
  //padding-left: 4rem;

  li {
    //display: grid;
    display: contents;
    font-size: 1.4rem;

    :nth-child(1) {
      justify-self: center;
    }

    .unit {
      margin-left: 4px;
      font-size: 0.875rem;
    }
  }

  .tree-limit img {
    width: 24px;
    filter: invert(1) brightness(0.8);
  }

  .person-limit img {
    width: 30px;
    filter: invert(1);
  }

  .content-limit svg {
    filter: invert(1) brightness(0.9);
  }

  .sharing-limit img {
    width: 26px;
    filter: brightness(2.2);
  }

  .integration img {
    width: 28px;
  }
}

.footnote {
  margin: 2rem 0;
  font-size: 0.75rem;
  font-style: italic;
  color: #888;
}

</style>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { hideModalAsync, usePageTitle } from '@/util/AppUtil'
import { defaultPlanId, getPlanTermPrice, Subscription, SubscriptionPlan, subscriptionPlans } from '@/gp/GroupAdminModel'
import { useSubscriptionStore } from '@/gp/SubscriptionStore'
import { useTokenManager } from '@/auth/TokenManager'
import { ImageIcon } from '@zhuowenli/vue-feather-icons'
import { ByteSizeUtil } from '@/util/ByteSizeUtil'
import SubscriptionConfirmPlanModal from '@/account/SubscriptionConfirmPlanModal.vue'
import PaymentModal from '@/account/PaymentModal.vue'
import { pluralize } from '@/util/LanguageUtil'

const loadingRef = ref(false)
const subRef = ref<Readonly<Subscription>>()
const selectedPlanIdRef = ref(defaultPlanId)
const confirmModalRef = ref<InstanceType<typeof SubscriptionConfirmPlanModal>>()
const paymentModalRef = ref<InstanceType<typeof PaymentModal>>()

const subStore = useSubscriptionStore()
const router = useRouter()
const tokenManager = useTokenManager()

usePageTitle('Plans')

const plansRef = computed(() => subscriptionPlans.filter(p => p.baseType != 'pro'))
const isAnonRef = computed(() => !tokenManager.current.isValid)
const canEditRef = computed(() => !isAnonRef.value && !tokenManager.current.isSupervised)
const currentPlanIdRef = computed(() => subRef.value?.items.at(0)?.price?.productId ?? defaultPlanId)

onMounted(async () => {
  loadingRef.value = true
  try {
    const subs = await subStore.getSubscriptionListForUserAsync(undefined)
    subRef.value = subs?.at(0) // HACK: assume max 1 subscription for now
  }
  finally {
    loadingRef.value = false
  }
})

watch(subRef, () => {
  selectedPlanIdRef.value = currentPlanIdRef.value
}, { immediate: true })

function getPlanContentLimit(plan: SubscriptionPlan) {
  const limitBytes = plan.contentLimit * (1024 * 1024) // MB to bytes
  return ByteSizeUtil.toReadableParts(limitBytes)
}

function onSelectPlan(plan: SubscriptionPlan) {
  if (isAnonRef.value) {
    // sign up first, then come back here
    router.push({ path: '/signup', query: { redirect_uri: '/plans' } })
  }
  else {
    confirmModalRef.value?.open(plan.id)
  }
}

async function addAsync(newPlanId: string) {
  try {
    const addResponse = await subStore.addAsync(newPlanId)
    await hideModalAsync(confirmModalRef.value!.$el)
    paymentModalRef.value!.open(addResponse.paymentIntentId!, addResponse.clientSecret!)
  }
  catch (error) {
    confirmModalRef.value?.addFailed()
  }
}
</script>
